import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { 
  Building2, Users, Plus, Search, Mail, Trash2, Shield, 
  UserPlus, ChevronDown, ChevronUp, Edit, Check, X, AlertCircle,
  DollarSign, Save
} from 'lucide-react';
import '../../styles/AdminStyles.css';

const OrganizationsAdmin = () => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOrg, setExpandedOrg] = useState(null);
  
  // New organization form state
  const [showNewOrgForm, setShowNewOrgForm] = useState(false);
  const [newOrg, setNewOrg] = useState({
    name: '',
    slug: '',
    description: ''
  });
  
  // Invite member form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'viewer'
  });
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Billing settings state
  const [billingSettings, setBillingSettings] = useState({});
  const [billingDirty, setBillingDirty] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingSaved, setBillingSaved] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (expandedOrg) {
      fetchMembers(expandedOrg);
      // Load billing settings for this org
      const org = organizations.find(o => o.organization_id === expandedOrg);
      if (org) {
        setBillingSettings({
          platform_submission_fee: org.platform_submission_fee ?? 4,
          platform_membership_fee: org.platform_membership_fee ?? 5,
          stripe_fee_percent: org.stripe_fee_percent ?? 2.9,
          stripe_fee_fixed: org.stripe_fee_fixed ?? 0.30,
          pass_stripe_fees_to_org: org.pass_stripe_fees_to_org ?? true,
        });
        setBillingDirty(false);
        setBillingSaved(false);
      }
    }
  }, [expandedOrg]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organizations')
        .select(`
          *,
          organization_members(count)
        `)
        .order('name');

      if (error) throw error;
      setOrganizations(data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (orgId) => {
    try {
      setMembersLoading(true);
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          *,
          users:user_uuid(uuid, email, first_name, last_name, profile_img),
          invited_by_user:invited_by(email, first_name, last_name)
        `)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setMembersLoading(false);
    }
  };

  const createOrganization = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Auto-generate slug if not provided
      const slug = newOrg.slug || newOrg.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      
      const { data, error } = await supabase
        .from('organizations')
        .insert([{
          name: newOrg.name,
          slug: slug,
          description: newOrg.description,
          created_by: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      // Auto-add creator as owner
      await supabase
        .from('organization_members')
        .insert([{
          organization_id: data.organization_id,
          user_uuid: user.id,
          role: 'owner',
          invited_by: user.id,
          is_active: true,
          accepted_at: new Date().toISOString()
        }]);

      setNewOrg({ name: '', slug: '', description: '' });
      setShowNewOrgForm(false);
      fetchOrganizations();
    } catch (error) {
      console.error('Error creating organization:', error);
      alert('Error creating organization: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inviteMember = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    setSubmitting(true);

    try {
      // Find user by email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('uuid, email, first_name, last_name')
        .eq('email', inviteData.email.toLowerCase().trim())
        .single();

      if (userError || !userData) {
        throw new Error('No user found with that email address. They must have a VibrantCast account first.');
      }

      // Check if already a member
      const { data: existingMember } = await supabase
        .from('organization_members')
        .select('id')
        .eq('organization_id', expandedOrg)
        .eq('user_uuid', userData.uuid)
        .single();

      if (existingMember) {
        throw new Error('This user is already a member of this organization.');
      }

      // Add member
      const { error: insertError } = await supabase
        .from('organization_members')
        .insert([{
          organization_id: expandedOrg,
          user_uuid: userData.uuid,
          role: inviteData.role,
          invited_by: user.id,
          is_active: true,
          accepted_at: new Date().toISOString()
        }]);

      if (insertError) throw insertError;

      setInviteSuccess(`Successfully added ${userData.first_name || userData.email} as ${inviteData.role}!`);
      setInviteData({ email: '', role: 'member' });
      fetchMembers(expandedOrg);
      fetchOrganizations(); // Refresh counts
      
      // Clear success message after 3 seconds
      setTimeout(() => setInviteSuccess(''), 3000);

    } catch (error) {
      setInviteError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const updateBillingSettings = async () => {
    setBillingSaving(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          platform_submission_fee: parseFloat(billingSettings.platform_submission_fee) || 0,
          platform_membership_fee: parseFloat(billingSettings.platform_membership_fee) || 0,
          stripe_fee_percent: parseFloat(billingSettings.stripe_fee_percent) || 0,
          stripe_fee_fixed: parseFloat(billingSettings.stripe_fee_fixed) || 0,
          pass_stripe_fees_to_org: billingSettings.pass_stripe_fees_to_org,
        })
        .eq('organization_id', expandedOrg);

      if (error) throw error;
      setBillingDirty(false);
      setBillingSaved(true);
      fetchOrganizations(); // refresh cached data
      setTimeout(() => setBillingSaved(false), 3000);
    } catch (error) {
      console.error('Error updating billing settings:', error);
      alert('Error saving billing settings: ' + error.message);
    } finally {
      setBillingSaving(false);
    }
  };

  const handleBillingChange = (field, value) => {
    setBillingSettings(prev => ({ ...prev, [field]: value }));
    setBillingDirty(true);
    setBillingSaved(false);
  };

  const updateMemberRole = async (memberId, newRole) => {
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;
      fetchMembers(expandedOrg);
    } catch (error) {
      console.error('Error updating member role:', error);
      alert('Error updating role: ' + error.message);
    }
  };

  const removeMember = async (memberId, memberEmail) => {
    if (!confirm(`Are you sure you want to remove ${memberEmail} from this organization?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
      fetchMembers(expandedOrg);
      fetchOrganizations(); // Refresh counts
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Error removing member: ' + error.message);
    }
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return Shield;
      case 'admin': return Users;
      default: return Users;
    }
  };

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-spinner">Loading organizations...</div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <Building2 className="admin-header-icon" />
          <div>
            <h1 className="admin-title">Organizations</h1>
            <p className="admin-subtitle">Manage client organizations and their team members</p>
          </div>
        </div>
        <button 
          onClick={() => setShowNewOrgForm(true)}
          className="admin-button primary"
        >
          <Plus size={16} />
          New Organization
        </button>
      </div>

      {/* Search */}
      <div className="admin-search-bar">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* New Organization Form */}
      {showNewOrgForm && (
        <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Plus size={18} />
            Create New Organization
          </h3>
          <form onSubmit={createOrganization}>
            <div className="form-group">
              <label>Organization Name *</label>
              <input
                type="text"
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                placeholder="e.g., Chicago Alliance of Visual Artists"
                required
              />
            </div>
            <div className="form-group">
              <label>Slug (optional, auto-generated)</label>
              <input
                type="text"
                value={newOrg.slug}
                onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                placeholder="e.g., cava"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newOrg.description}
                onChange={(e) => setNewOrg({ ...newOrg, description: e.target.value })}
                placeholder="Brief description of the organization"
                rows={3}
              />
            </div>
            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => setShowNewOrgForm(false)}
                className="admin-button secondary"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="admin-button primary"
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Organizations List */}
      <div className="admin-list">
        {filteredOrganizations.length === 0 ? (
          <div className="admin-empty-state">
            <Building2 size={48} />
            <h3>No organizations yet</h3>
            <p>Create your first organization to get started</p>
          </div>
        ) : (
          filteredOrganizations.map(org => (
            <div key={org.organization_id} className="admin-list-item expandable">
              <div 
                className="admin-list-item-header"
                onClick={() => setExpandedOrg(expandedOrg === org.organization_id ? null : org.organization_id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="admin-list-item-info">
                  <Building2 size={20} />
                  <div>
                    <h4>{org.name}</h4>
                    <p className="text-muted">
                      {org.slug} • {org.organization_members?.[0]?.count || 0} members
                    </p>
                  </div>
                </div>
                <div className="admin-list-item-actions">
                  {expandedOrg === org.organization_id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>

              {/* Expanded Content - Members */}
              {expandedOrg === org.organization_id && (
                <div className="admin-list-item-content" style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
                  {/* Billing Settings */}
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <h5 style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#166534' }}>
                      <DollarSign size={16} />
                      Billing Settings
                    </h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>Submission Fee (per artist)</label>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '0.875rem' }}>$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={billingSettings.platform_submission_fee ?? ''}
                            onChange={(e) => handleBillingChange('platform_submission_fee', e.target.value)}
                            style={{ width: '100%', padding: '0.5rem 0.5rem 0.5rem 1.25rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>Membership Fee (per member)</label>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '0.875rem' }}>$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={billingSettings.platform_membership_fee ?? ''}
                            onChange={(e) => handleBillingChange('platform_membership_fee', e.target.value)}
                            style={{ width: '100%', padding: '0.5rem 0.5rem 0.5rem 1.25rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>Stripe Fee %</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={billingSettings.stripe_fee_percent ?? ''}
                            onChange={(e) => handleBillingChange('stripe_fee_percent', e.target.value)}
                            style={{ width: '100%', padding: '0.5rem 0.5rem 0.5rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                          />
                          <span style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '0.875rem' }}>%</span>
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>Stripe Fixed Fee</label>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '0.875rem' }}>$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={billingSettings.stripe_fee_fixed ?? ''}
                            onChange={(e) => handleBillingChange('stripe_fee_fixed', e.target.value)}
                            style={{ width: '100%', padding: '0.5rem 0.5rem 0.5rem 1.25rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={billingSettings.pass_stripe_fees_to_org ?? true}
                          onChange={(e) => handleBillingChange('pass_stripe_fees_to_org', e.target.checked)}
                        />
                        Pass Stripe processing fees to organization
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                          {billingSettings.pass_stripe_fees_to_org ? '(org pays Stripe fees)' : '(VibrantCast absorbs Stripe fees)'}
                        </span>
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {billingSaved && (
                          <span style={{ color: '#059669', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Check size={14} /> Saved
                          </span>
                        )}
                        <button
                          onClick={updateBillingSettings}
                          disabled={!billingDirty || billingSaving}
                          className="admin-button primary"
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: billingDirty ? 1 : 0.5 }}
                        >
                          <Save size={14} />
                          {billingSaving ? 'Saving...' : 'Save Billing Settings'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Invite Form */}
                  <div className="invite-section" style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                    <h5 style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <UserPlus size={16} />
                      Add Team Member
                    </h5>
                    <form onSubmit={inviteMember} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <input
                        type="email"
                        placeholder="Email address"
                        value={inviteData.email}
                        onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                        style={{ flex: '1', minWidth: '200px', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                        required
                      />
                      <select
                        value={inviteData.role}
                        onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                        style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                      >
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                      <button 
                        type="submit" 
                        className="admin-button primary"
                        disabled={submitting}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      >
                        <Mail size={14} />
                        {submitting ? 'Adding...' : 'Add Member'}
                      </button>
                    </form>
                    {inviteError && (
                      <div style={{ marginTop: '0.5rem', color: '#dc2626', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <AlertCircle size={14} />
                        {inviteError}
                      </div>
                    )}
                    {inviteSuccess && (
                      <div style={{ marginTop: '0.5rem', color: '#059669', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Check size={14} />
                        {inviteSuccess}
                      </div>
                    )}
                  </div>

                  {/* Members List */}
                  <h5 style={{ marginBottom: '0.75rem' }}>
                    Team Members ({members.length})
                  </h5>
                  {membersLoading ? (
                    <div className="text-muted">Loading members...</div>
                  ) : members.length === 0 ? (
                    <div className="text-muted">No members yet. Add team members above.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {members.map(member => (
                        <div 
                          key={member.id}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '0.75rem',
                            background: 'white',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div 
                              style={{ 
                                width: '36px', 
                                height: '36px', 
                                borderRadius: '50%', 
                                background: '#e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden'
                              }}
                            >
                              {member.users?.profile_img ? (
                                <img 
                                  src={member.users.profile_img} 
                                  alt="" 
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <Users size={18} style={{ color: '#9ca3af' }} />
                              )}
                            </div>
                            <div>
                              <div style={{ fontWeight: '500' }}>
                                {member.users?.first_name && member.users?.last_name 
                                  ? `${member.users.first_name} ${member.users.last_name}`
                                  : member.users?.email || 'Unknown User'}
                              </div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                {member.users?.email}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <select
                              value={member.role}
                              onChange={(e) => updateMemberRole(member.id, e.target.value)}
                              style={{ 
                                padding: '0.25rem 0.5rem', 
                                fontSize: '0.875rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px'
                              }}
                            >
                              <option value="viewer">Viewer</option>
                              <option value="editor">Editor</option>
                              <option value="admin">Admin</option>
                              <option value="owner">Owner</option>
                            </select>
                            <button
                              onClick={() => removeMember(member.id, member.users?.email)}
                              style={{ 
                                padding: '0.25rem',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#dc2626'
                              }}
                              title="Remove member"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrganizationsAdmin;
